import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Building2, Sparkles, CheckCircle, AlertCircle, Loader2, ArrowRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { ROUTES } from '../../config/constants';

interface OrganizationSetupData {
  companyName: string;
  industry: string;
  companySize: string;
  userRole: string;
}

export function OrganizationSetup() {
  const { session } = useAuth();
  const navigate = useNavigate();
  
  // Get company name from user metadata if available
  const savedCompanyName = session?.user?.user_metadata?.company_name || '';
  
  const [setupData, setSetupData] = useState<OrganizationSetupData>({
    companyName: savedCompanyName,
    industry: '',
    companySize: '',
    userRole: ''
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState<string>('Setting up...');

  const totalSteps = 3;
  const progress = (currentStep / totalSteps) * 100;

  const handleNext = () => {
    setError(null);
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setError(null);
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinishSetup = async () => {
    setError(null);
    setIsLoading(true);

    if (!setupData.companyName.trim()) {
      setError('Company name is required');
      setIsLoading(false);
      return;
    }

    try {
      console.log('Setting up organization:', setupData);
      
      // Update loading message for each step
      setLoadingMessage('Creating your organization...');

      // Call the existing setup-organization function
      const { data, error: setupError } = await supabase.functions.invoke('setup-organization', {
        body: {
          companyName: setupData.companyName,
          userId: session?.user?.id,
          fullName: session?.user?.user_metadata?.full_name || session?.user?.email?.split('@')[0],
          userEmail: session?.user?.email
        }
      });

      if (setupError) {
        console.error('Organization setup error:', setupError);
        throw setupError;
      }

      // Validate response
      if (!data || !data.success) {
        throw new Error(data?.error || 'Organization setup failed');
      }

      setLoadingMessage('Finalizing your workspace...');
      console.log('Organization setup successful:', data);

      // Wait longer to ensure triggers complete and data is available
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Force a full page reload to clear all caches and refetch data
      // This prevents duplicate organizations from being created
      window.location.href = ROUTES.DASHBOARD;

    } catch (err) {
      console.error('Organization setup error:', err);
      
      if (err instanceof Error) {
        // Provide more specific error messages
        const errorMessage = err.message.toLowerCase();
        if (errorMessage.includes('already exists') || errorMessage.includes('unique constraint')) {
          setError('An organization with this name may already exist. Please try a different name.');
        } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
          setError('Network error. Please check your connection and try again.');
        } else if (errorMessage.includes('unauthorized') || errorMessage.includes('forbidden')) {
          setError('Authentication error. Please sign in again.');
        } else {
          setError(`Setup failed: ${err.message}`);
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return setupData.companyName.trim().length >= 2;
      case 2:
        return setupData.industry.trim().length > 0 && setupData.companySize.trim().length > 0;
      case 3:
        return setupData.userRole.trim().length > 0;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto h-12 w-12 bg-primary rounded-xl flex items-center justify-center mb-4">
            <Building2 className="h-6 w-6 text-primary-foreground" />
          </div>
          <CardTitle className="text-2xl font-bold">Welcome to TopMatchTalent!</CardTitle>
          <CardDescription className="text-base">
            Let's set up your organization to get started with your hiring platform.
          </CardDescription>
          
          {/* Progress Bar */}
          <div className="mt-6 space-y-2">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Step {currentStep} of {totalSteps}</span>
              <span>{Math.round(progress)}% complete</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        </CardHeader>

        <CardContent className="pt-6">
          {/* Step 1: Company Information */}
          {currentStep === 1 && (
            <div className="space-y-6 animate-in fade-in-0 slide-in-from-right-2 duration-300">
              <div className="text-center">
                <h3 className="text-xl font-semibold">Company Information</h3>
                <p className="text-muted-foreground mt-1">Tell us about your organization</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="companyName">Company Name</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="companyName"
                      type="text"
                      placeholder="Your Company Inc."
                      value={setupData.companyName}
                      onChange={(e) => setSetupData(prev => ({ ...prev, companyName: e.target.value }))}
                      required
                      className="pl-9"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    This will be the name of your organization in the platform
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Company Details */}
          {currentStep === 2 && (
            <div className="space-y-6 animate-in fade-in-0 slide-in-from-right-2 duration-300">
              <div className="text-center">
                <h3 className="text-xl font-semibold">Company Details</h3>
                <p className="text-muted-foreground mt-1">Help us customize your experience</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Input
                    id="industry"
                    type="text"
                    placeholder="Technology, Healthcare, Finance..."
                    value={setupData.industry}
                    onChange={(e) => setSetupData(prev => ({ ...prev, industry: e.target.value }))}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="companySize">Company Size</Label>
                  <select
                    id="companySize"
                    value={setupData.companySize}
                    onChange={(e) => setSetupData(prev => ({ ...prev, companySize: e.target.value }))}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    required
                  >
                    <option value="">Select company size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-500">201-500 employees</option>
                    <option value="501-1000">501-1000 employees</option>
                    <option value="1000+">1000+ employees</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Your Role & Finalization */}
          {currentStep === 3 && (
            <div className="space-y-6 animate-in fade-in-0 slide-in-from-right-2 duration-300">
              <div className="text-center">
                <h3 className="text-xl font-semibold">Your Role</h3>
                <p className="text-muted-foreground mt-1">What's your role in the organization?</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="userRole">Your Role/Title</Label>
                  <Input
                    id="userRole"
                    type="text"
                    placeholder="HR Manager, Recruiter, CEO..."
                    value={setupData.userRole}
                    onChange={(e) => setSetupData(prev => ({ ...prev, userRole: e.target.value }))}
                    required
                  />
                </div>

                {/* Setup Summary */}
                <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Sparkles className="h-4 w-4" />
                    Setup Summary
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Company:</span>
                      <span className="font-medium">{setupData.companyName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Industry:</span>
                      <span className="font-medium">{setupData.industry}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Size:</span>
                      <span className="font-medium">{setupData.companySize}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Your Role:</span>
                      <span className="font-medium">{setupData.userRole}</span>
                    </div>
                  </div>
                </div>

                {/* What happens next */}
                <div className="mt-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <h4 className="font-medium mb-2 text-primary">What happens next?</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    <li>✓ You'll become the admin of your organization</li>
                    <li>✓ You can start analyzing candidates immediately</li>
                    <li>✓ You can invite team members from the dashboard</li>
                    <li>✓ Access to all platform features and analytics</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive" className="mt-4 animate-in fade-in-0 slide-in-from-top-2">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between">
                <span>{error}</span>
                {error.includes('sign in again') && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => navigate(ROUTES.LOGIN)}
                    className="ml-4"
                  >
                    Sign In
                  </Button>
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            <Button 
              variant="outline" 
              onClick={handleBack} 
              disabled={currentStep === 1 || isLoading}
            >
              Back
            </Button>

            {currentStep < totalSteps ? (
              <Button 
                onClick={handleNext} 
                disabled={!canProceed() || isLoading}
              >
                Next Step
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button 
                onClick={handleFinishSetup} 
                disabled={!canProceed() || isLoading}
                className="min-w-[120px]"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {loadingMessage}
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Complete Setup
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 